import axios from 'axios'
import { useAuthStore } from '@/stores'
import { createToaster } from '@meforma/vue-toaster'
import { networkHandler } from '@/helpers/network-handler'

const toaster = createToaster({ position: 'top' })

const api = axios.create({
    baseURL: import.meta.env.VITE_DRIVER_URL,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true
})

api.interceptors.request.use(function (config) {
    const token = useAuthStore()?.user?.access_token

    config.headers.Authorization = token
    config.headers['X-Route-Payload'] = 'compact'

    return config
})

const isNetworkError = (error) => {
    return (
        !error.response &&
        (error.message === 'Network Error' ||
            error.code === 'ERR_NETWORK' ||
            error.code === 'ECONNABORTED')
    )
}

const handleError = async (error, originalRequest) => {
    const authStore = useAuthStore()
    let message = 'An error occurred'

    // Перевірка на помилку мережі
    if (isNetworkError(error)) {
        const isOnline = await networkHandler.checkConnection()

        if (!isOnline) {
            // Якщо немає інтернету, додаємо запит до черги
            return networkHandler.addRequest(async () => {
                return await api(originalRequest)
            })
        }
    }

    if (error.response) {
        const { status, data } = error.response

        if ([401, 403].includes(status) && !originalRequest._retry) {
            originalRequest._retry = true

            // Перевіряємо інтернет перед спробою рефрешу токена
            const isOnline = await networkHandler.checkConnection()

            if (!isOnline) {
                return networkHandler.addRequest(async () => {
                    const refreshResult = await authStore.refreshTokenDirect()

                    if (refreshResult.success) {
                        if (!originalRequest.headers) {
                            originalRequest.headers = {}
                        }
                        originalRequest.headers.Authorization = authStore.user?.access_token

                        return await api(originalRequest)
                    }
                    throw new Error('Token refresh failed')
                })
            }

            // Try to refresh token
            const refreshResult = await authStore.refreshTokenDirect()

            if (refreshResult.success) {
                if (!originalRequest.headers) {
                    originalRequest.headers = {}
                }
                originalRequest.headers.Authorization = authStore.user?.access_token

                // Retry the original request
                try {
                    const response = await api(originalRequest)

                    return response.data
                } catch (retryError) {
                    // Якщо повторний запит провалився через мережу, не розлогінюємо
                    if (isNetworkError(retryError)) {
                        return networkHandler.addRequest(async () => {
                            return await api(originalRequest)
                        })
                    }
                    error = retryError
                }
            } else if (refreshResult.isNetworkError || isNetworkError(error)) {
                // Якщо помилка мережі, додаємо в чергу весь флоу (refresh + original request)
                return networkHandler.addRequest(async () => {
                    const newRefreshResult = await authStore.refreshTokenDirect()

                    if (newRefreshResult.success) {
                        if (!originalRequest.headers) {
                            originalRequest.headers = {}
                        }
                        originalRequest.headers.Authorization = authStore.user?.access_token
                        const response = await api(originalRequest)

                        return response.data
                    }
                    throw new Error('Token refresh failed')
                })
            } else {
                // Розлогінюємо тільки якщо це не помилка мережі
                authStore.clearUserSession()
            }
        }
        message = data.message || message
    } else {
        message = error.message || message
    }

    const isSeatConfirm =
        error.response?.status === 409 &&
        (error.response?.data?.code === 'seats_over_capacity' ||
            error.response?.data?.confirm_required === true)

    // Не показуємо помилку, якщо це помилка мережі
    if (!isNetworkError(error) && !isSeatConfirm) {
        toaster.error(message)
    }

    throw error
}

const fetchWrapper = {
    async get(url, params = {}) {
        try {
            const response = await api.get(url, { params })

            return response.data
        } catch (error) {
            const config = { method: 'get', url, params }

            return await handleError(error, config)
        }
    },

    async post(url, data, options = {}) {
        try {
            const response = await api.post(url, data, options)

            return response.data
        } catch (error) {
            const config = { method: 'post', url, data, ...options }

            return await handleError(error, config)
        }
    },

    async put(url, data) {
        try {
            const response = await api.put(url, data)

            return response.data
        } catch (error) {
            const config = { method: 'put', url, data }

            return await handleError(error, config)
        }
    },

    async patch(url, data, options = {}) {
        try {
            const response = await api.patch(url, data, options)

            return response.data
        } catch (error) {
            const config = { method: 'patch', url, data, ...options }

            return await handleError(error, config)
        }
    },

    async delete(url, data) {
        try {
            const response = await api.delete(url, { data })

            return response.data
        } catch (error) {
            const config = { method: 'delete', url, data }

            return await handleError(error, config)
        }
    }
}

export { api }
export default fetchWrapper
